import type { FC } from 'react'

import { Button } from '@skytech/ui/Button'
import { Container } from '@skytech/ui/Container'
import { countryOptions, getWhiteLabelForDealerStyle } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'
import ArrowLeftIcon from 'mdi-react/ArrowLeftIcon'
import { useParams, useNavigate } from 'react-router-dom'

import { demoCustomersResources } from '@/api/demoCustomersApi'
import queries from '@/api/queries'
import { useAppContext } from '@/App.context'
import Page from '@/components/content/Page'
import { useTranslation } from '@/components/i18n/hooks'
import { Translation } from '@/components/i18n/Translation'
import DataPoint, { DataPoints } from '@/components/primitives/DataPoint'
import LoaderCircle from '@/components/util/LoaderCircle'
import { appRoutes } from '@/util/misc'

import { DemoCustomerInfo } from './DemoCustomerInfo'

export const DemoCustomer: FC = () => {
  const { whiteLabel } = useAppContext()
  const translate = useTranslation()
  const navigate = useNavigate()

  const params = useParams()
  const demoCustomerId = Number(params.id)

  if (isNaN(demoCustomerId)) throw Error(translate('Demo customer ID is not a number!'))

  const { data: demoCustomers, isLoading } = useQuery(
    queries.demoCustomers(),
    demoCustomersResources.getList
  )

  const demoCustomer = demoCustomers?.findLast(el => el.id === demoCustomerId)

  if (!demoCustomer) {
    return (
      <Page className="demo-customer">
        <h1 className="center">
          <Translation id="Demo customer with this ID not found" />
        </h1>
        <div className="center">
          <Button
            className="ml-0 pl-0"
            size="small"
            variant="tertiary"
            onClick={() => navigate(appRoutes.salesSupport)}
            Icon={<ArrowLeftIcon />}
            whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
          >
            <Translation id="Back to Sales support" />
          </Button>
        </div>
      </Page>
    )
  }

  const firstUserCountryId = demoCustomer?.costObjects[0]?.countryId
  const countryName =
    countryOptions.find(
      (country: { value: number; label: string }) => country.value === firstUserCountryId
    )?.label || ''

  return (
    <Page>
      <Button
        className="ml-0 pl-0"
        size="small"
        variant="tertiary"
        onClick={() => navigate(appRoutes.salesSupport)}
        Icon={<ArrowLeftIcon />}
        whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
      >
        <Translation id="Back" />
      </Button>
      <h1>
        <Translation id="Demo customer details" />
      </h1>
      {isLoading ? (
        <LoaderCircle />
      ) : (
        <>
          {demoCustomer && (
            <>
              <Container className="shadow-basic mx-0 my-4 rounded-lg bg-white p-4">
                <h3 className="mt-0">
                  <Translation id="Customer Information" />
                </h3>
                <DataPoints>
                  <DataPoint header={<Translation id="Local" />} value={demoCustomer.name} />
                  <DataPoint
                    header={<Translation id="Global" />}
                    value={demoCustomer.customerHeadName}
                  />
                  {countryName && (
                    <DataPoint header={<Translation id="Country" />} value={countryName} />
                  )}
                </DataPoints>
              </Container>

              <DemoCustomerInfo demoCustomer={demoCustomer} />
            </>
          )}
        </>
      )}
    </Page>
  )
}
